export const SERVER_TIMEZONE = process.env.REACT_APP_SERVER_TIMEZONE || 'America/New_York'
export const API_BASE = `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_HOST_VERSION}`
export const GOOGLE_MAPS_API_KEY = `${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
export const CUSTODIAL_FEE = [{ label: "0", value: 0 }, { label: "50", value: 50 }, { label: "100", value: 100 }]
export const PAYMENT_SCHEDULE = [
  {
    label: 'Weekly',
    value: 'weekly'
  },
  {
    label: 'Bi-Weekly',
    value: 'every2'
  },
  {
    label: 'Semi-Monthly',
    value: 'semiMonthly'
  },
  {
    label: 'Monthly',
    value: 'monthly'
  },
  {
    label: 'Custom',
    value: 'custom'
  }
]

export const frequencyLabel = (value) => PAYMENT_SCHEDULE.find((item) => item.value === value)?.label

export const MONTHS = [
  { label: 'January', value: 1 },
  { label: 'February', value: 2 },
  { label: 'March', value: 3 },
  { label: 'April', value: 4 },
  { label: 'May', value: 5 },
  { label: 'June', value: 6 },
  { label: 'July', value: 7 },
  { label: 'August', value: 8 },
  { label: 'September', value: 9 },
  { label: 'October', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 }
]

export const DAYS = Array.from({ length: 31 }, (_, i) => ({
  label: `${i + 1}`,
  value: i + 1
}))

export const AVAILABLE_PROFILE_STATUS = ['10', '11', '12', '13', 'A1', 'A2']
export const SENDER_EMAIL = 'support@mydebtlawyers.com'
export const IS_DEV_ENV = process.env.REACT_APP_IS_DEV_ENV

export const PLAN_LENGTH_MENU = ({ userRole, isAffiliateManager = false }) => {
  let length

  if (userRole === 'customer' || userRole === 'user') {
    length = 120
  } else if (userRole === 'affiliateGroupsAdmin') {
    length = 60
  } else {
    length = 48
  }

  return Array.from({ length }, (_, i) => ({
    label: `${i + 1} months`,
    value: i + 1
  }))
}

export const DEFAULT_MIN_MONTHLY_PAYMENT = 200

export const FDR_PAYMENT_TIERS = [
  { min: 6000, max: 10000, minPayment: 200 },
  { min: 10000, max: 12500, minPayment: 225 },
  { min: 12500, max: 15000, minPayment: 250 },
  { min: 15000, max: 17500, minPayment: 275 },
  { min: 17500, max: 20000, minPayment: 300 },
  { min: 20000, max: 22500, minPayment: 325 },
  { min: 22500, max: 25000, minPayment: 350 },
  { min: 25000, max: 27500, minPayment: 375 },
  { min: 27500, max: 30000, minPayment: 400 },
  { min: 30000, max: 32500, minPayment: 425 },
  { min: 32500, max: 35000, minPayment: 450 },
  { min: 35000, max: 37500, minPayment: 475 },
  { min: 37500, max: 40000, minPayment: 500 },
  { min: 40000, max: 75000, minPayment: 500 },
  { min: 75000, max: Infinity, minPayment: 500, maxTerm: 60 }
]

export const FDR_DEFAULT_MAX_TERM = 48
export const FDR_HIGH_DEBT_THRESHOLD = 75000
export const FDR_ONE_PERCENT_THRESHOLD = 20000

export const getOnePercentFloor = (enrolledAmount) => {
  const cents = Math.round(enrolledAmount * 100)

  return cents > FDR_ONE_PERCENT_THRESHOLD * 100 ? Math.round(cents / 100) / 100 : 0
}

export const getFDRMinimumPayment = (enrolledAmount, { onePercentFloor = false } = {}) => {
  if (!enrolledAmount) return DEFAULT_MIN_MONTHLY_PAYMENT

  const tier = FDR_PAYMENT_TIERS.find(
    (t) => enrolledAmount >= t.min && enrolledAmount < t.max
  )
  const tierMinimum = tier ? tier.minPayment : DEFAULT_MIN_MONTHLY_PAYMENT

  return onePercentFloor ? Math.max(tierMinimum, getOnePercentFloor(enrolledAmount)) : tierMinimum
}

export const formatMinimumPayment = (value) => Number.isInteger(value) ? `${value}` : value.toFixed(2)

// A flat fee is often taken in a single charge, so 1 is now a routine value here rather than an
// edge — "1 payments" reads as a bug to the rep looking at it.
export const paymentsLabel = (n) => `${n} ${Number(n) === 1 ? 'payment' : 'payments'}`

export const getFrequencyBasedProgramLength = (frequency, enrolledAmount = 0, userRole = '', isAffiliateManager = false) => {
  let maxTerm = 48
  let minTerm = 1

  if (userRole === 'customer' || userRole === 'user') {
    maxTerm = 120
  } else if (userRole === 'affiliateGroupsAdmin') {
    maxTerm = 60
  } else if (enrolledAmount >= FDR_HIGH_DEBT_THRESHOLD) {
    maxTerm = 60
  }

  const generateMonthOptions = (max, min = 1) => {
    const options = []
    for (let m = min; m <= max; m++) {
      options.push(m)
    }
    return options
  }

  switch (frequency) {
    case 'monthly':
      const monthlyOptions = generateMonthOptions(maxTerm, minTerm)
      return monthlyOptions.map(m => ({ label: paymentsLabel(m), value: m }))

    case 'every2':
      if (minTerm === 1) {
        const maxBiWeekly = maxTerm * 2
        return Array.from({ length: maxBiWeekly }, (_, i) => ({
          label: paymentsLabel(i + 1),
          value: i + 1
        }))
      } else {
        const biWeeklyMonths = generateMonthOptions(maxTerm, minTerm)
        return biWeeklyMonths.map(m => {
          const payments = m * 2
          return { label: paymentsLabel(payments), value: payments }
        })
      }

    case 'weekly':
      if (minTerm === 1) {
        const maxWeekly = maxTerm * 4
        return Array.from({ length: maxWeekly }, (_, i) => ({
          label: paymentsLabel(i + 1),
          value: i + 1
        }))
      } else {
        const weeklyMonths = generateMonthOptions(maxTerm, minTerm)
        return weeklyMonths.map(m => {
          const payments = m * 4
          return { label: paymentsLabel(payments), value: payments }
        })
      }

    case 'semiMonthly':
      const semiMonthlyOptions = generateMonthOptions(maxTerm, minTerm)
      return semiMonthlyOptions.map(m => ({ label: `${m} months`, value: m }))

    case 'custom':
      const customOptions = generateMonthOptions(maxTerm, minTerm)
      return customOptions.map(m => ({ label: `${m} months`, value: m }))

    default:
      return []
  }
}

export const FREQUENCY_BASED_PROGRAM_LENGTH = {
  monthly: [
    { label: '12 months', value: 12 },
    { label: '15 months', value: 15 },
    { label: '18 months', value: 18 },
    { label: '21 months', value: 21 },
    { label: '24 months', value: 24 },
    { label: '30 months', value: 30 },
    { label: '36 months', value: 36 },
    { label: '42 months', value: 42 },
    { label: '48 months', value: 48 }
  ],
  every2: [
    { label: '26 weeks', value: 26 },
    { label: '33 weeks', value: 33 },
    { label: '39 weeks', value: 39 },
    { label: '46 weeks', value: 46 },
    { label: '52 weeks', value: 52 },
    { label: '65 weeks', value: 65 },
    { label: '78 weeks', value: 78 },
    { label: '91 weeks', value: 91 },
    { label: '104 weeks', value: 104 }
  ],
  weekly: [
    { label: '52 weeks', value: 52 },
    { label: '65 weeks', value: 65 },
    { label: '78 weeks', value: 78 },
    { label: '91 weeks', value: 91 },
    { label: '104 weeks', value: 104 },
    { label: '130 weeks', value: 130 },
    { label: '156 weeks', value: 156 },
    { label: '182 weeks', value: 182 },
    { label: '208 weeks', value: 208 }
  ],
  semiMonthly: [
    { label: '12 months', value: 12 },
    { label: '15 months', value: 15 },
    { label: '18 months', value: 18 },
    { label: '21 months', value: 21 },
    { label: '24 months', value: 24 },
    { label: '30 months', value: 30 },
    { label: '36 months', value: 36 },
    { label: '42 months', value: 42 },
    { label: '48 months', value: 48 }
  ],
  custom: [
    { label: '12 months', value: 12 },
    { label: '18 months', value: 18 },
    { label: '24 months', value: 24 },
    { label: '30 months', value: 30 },
    { label: '36 months', value: 36 },
    { label: '42 months', value: 42 },
    { label: '48 months', value: 48 }
  ]
}